/**
 * @fileoverview Viewport-aware positioning helpers for fixed popups, dropdowns,
 *               context menus, and flyouts in the FireSquirrel web apps.
 * @module ui-admin/utils/viewportPosition
 *
 * Responsibilities:
 * - Provide a single source of truth for clamping popup positions against the
 *   browser viewport.
 * - Expose pure functions for anchoring popups to elements, cursors, or rects.
 * - Return style objects that use a CSS custom property so the popup's
 *   max-height automatically shrinks on short screens and never extends below
 *   the visible viewport.
 *
 * Dependencies:
 * - React types (React.CSSProperties)
 * - Standard DOM APIs (getBoundingClientRect, window.innerWidth/innerHeight)
 *
 * Architecture:
 * - Pure geometry utilities (no DOM side effects except reading rects).
 * - The clamping logic tries the requested placement first, then flips
 *   horizontally/vertically if it overflows, and finally pins to the viewport
 *   edge with a configurable margin as a last resort.
 *
 * Notes:
 * - Callers supply an estimated width/height for clamping; the returned style
 *   applies a dynamic max-height so the real rendered popup will scroll if its
 *   content is taller than the available viewport space.
 * - All returned positions are screen coordinates suitable for `position: fixed`.
 */

import type { RefObject } from 'react';

/** Viewport margin kept between a popup and any window edge. */
export const POPUP_VIEWPORT_MARGIN = 8;

/** Default minimum visible height to preserve when a popup is pushed down. */
export const POPUP_MIN_VISIBLE_HEIGHT = 120;

/** CSS custom property used to communicate the popup top to the max-height rule. */
export const POPUP_TOP_CSS_VAR = '--positioned-popup-top';

/** Supported placement strategies for popup positioning. */
export type PopupPlacement =
  | 'bottom-start'
  | 'bottom-end'
  | 'top-start'
  | 'top-end'
  | 'right-start'
  | 'right-end'
  | 'left-start'
  | 'left-end'
  | 'cursor';

/** A rectangle representing the popup anchor in screen coordinates. */
export interface AnchorRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

/** A cursor point or any object with `x`/`y` screen coordinates. */
export interface CursorPoint {
  x: number;
  y: number;
}

/** Estimated dimensions used for clamping before the popup is rendered. */
export interface PopupDimensions {
  /** Exact/estimated width of the popup in pixels. */
  width: number;
  /** Estimated overall height of the popup content in pixels. */
  estimatedHeight?: number;
  /** Minimum height that should remain visible if possible. */
  minHeight?: number;
}

/** Viewport size used for clamping. */
export interface ViewportSize {
  width: number;
  height: number;
}

/** Options for clamping a popup against the viewport. */
export interface ClampPopupOptions {
  /** Gap between the anchor and the popup in pixels. */
  offset?: number;
  /** Margin to keep from the viewport edge. */
  margin?: number;
  /** Viewport dimensions. Defaults to `window.innerWidth/Height`. */
  viewport?: ViewportSize;
  /** Minimum height to preserve when flipping/clamping. */
  minHeight?: number;
}

export type PopupAnchor = HTMLElement | RefObject<HTMLElement | null> | CursorPoint;

/**
 * @contract ClampPopupResult
 * Purpose: Screen coordinates and metadata for a popup that has been clamped
 *          to the visible viewport.
 * Fields:  left, top, placement
 * Rules:   left/top are in client/screen coordinates and are safe to pass to
 *          `position: fixed` elements. placement indicates the placement that
 *          was actually used after flipping.
 * Sync:    n/a
 */
export interface ClampPopupResult {
  left: number;
  top: number;
  placement: PopupPlacement;
}

/**
 * Read the anchor rectangle from a DOM element, React ref, or cursor point.
 *
 * @param anchor - The anchor to resolve.
 * @returns A rectangle in screen coordinates.
 */
export function getAnchorRect(anchor: PopupAnchor | AnchorRect): AnchorRect {
  if (isAnchorRect(anchor)) {
    return anchor;
  }

  if (isRefObject(anchor)) {
    const el = anchor.current;
    if (el) return el.getBoundingClientRect();
  }

  if (isCursorPoint(anchor)) {
    return {
      left: anchor.x,
      top: anchor.y,
      right: anchor.x,
      bottom: anchor.y,
      width: 0,
      height: 0,
    };
  }

  if (anchor && 'getBoundingClientRect' in (anchor as object)) {
    return (anchor as HTMLElement).getBoundingClientRect();
  }

  return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
}

function isAnchorRect(value: unknown): value is AnchorRect {
  return (
    typeof value === 'object' &&
    value !== null &&
    'left' in value &&
    'top' in value &&
    'right' in value &&
    'bottom' in value &&
    'width' in value &&
    'height' in value
  );
}

function isRefObject(value: unknown): value is RefObject<HTMLElement | null> {
  return typeof value === 'object' && value !== null && 'current' in value;
}

function isCursorPoint(value: unknown): value is CursorPoint {
  return (
    typeof value === 'object' &&
    value !== null &&
    'x' in value &&
    'y' in value &&
    typeof (value as CursorPoint).x === 'number' &&
    typeof (value as CursorPoint).y === 'number'
  );
}

function getDefaultViewport(): ViewportSize {
  if (typeof window === 'undefined') return { width: 1024, height: 768 };
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Compute an ordered list of candidate placements to try for a popup.
 *
 * The first candidate is the requested placement. The following entries are the
 * sensible flips (horizontal first, then vertical, then both) before the
 * final pin-to-edge fallback is applied by the caller.
 */
function getCandidatePlacements(placement: PopupPlacement): PopupPlacement[] {
  const map: Record<PopupPlacement, PopupPlacement[]> = {
    'bottom-start': ['bottom-start', 'bottom-end', 'top-start', 'top-end'],
    'bottom-end': ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
    'top-start': ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
    'top-end': ['top-end', 'top-start', 'bottom-end', 'bottom-start'],
    'right-start': ['right-start', 'right-end', 'left-start', 'left-end'],
    'right-end': ['right-end', 'right-start', 'left-end', 'left-start'],
    'left-start': ['left-start', 'left-end', 'right-start', 'right-end'],
    'left-end': ['left-end', 'left-start', 'right-end', 'right-start'],
    'cursor': ['cursor'],
  };
  return map[placement] ?? ['cursor'];
}

/**
 * Compute the raw (unclamped) position for a popup using a specific placement.
 */
function computePlacement(
  placement: PopupPlacement,
  anchor: AnchorRect,
  popup: PopupDimensions,
  offset: number,
): { left: number; top: number } {
  switch (placement) {
    case 'bottom-start':
      return { left: anchor.left, top: anchor.bottom + offset };
    case 'bottom-end':
      return { left: anchor.right - popup.width, top: anchor.bottom + offset };
    case 'top-start':
      return { left: anchor.left, top: anchor.top - (popup.estimatedHeight ?? popup.minHeight ?? 0) - offset };
    case 'top-end':
      return {
        left: anchor.right - popup.width,
        top: anchor.top - (popup.estimatedHeight ?? popup.minHeight ?? 0) - offset,
      };
    case 'right-start':
      return { left: anchor.right + offset, top: anchor.top };
    case 'right-end':
      return {
        left: anchor.right + offset,
        top: anchor.bottom - (popup.estimatedHeight ?? popup.minHeight ?? 0),
      };
    case 'left-start':
      return { left: anchor.left - popup.width - offset, top: anchor.top };
    case 'left-end':
      return {
        left: anchor.left - popup.width - offset,
        top: anchor.bottom - (popup.estimatedHeight ?? popup.minHeight ?? 0),
      };
    case 'cursor':
      return { left: anchor.left, top: anchor.top };
    default:
      return { left: anchor.left, top: anchor.bottom + offset };
  }
}

/**
 * Clamp a popup's left/top coordinates so they stay within the viewport.
 *
 * Tries the requested placement and its natural flips. If none fit, it pins the
 * popup to the viewport edge while preserving the configured margin.
 *
 * @param anchor - Anchor element, ref, cursor point, or rectangle in screen coordinates.
 * @param popup - Estimated dimensions of the popup.
 * @param placement - Desired placement relative to the anchor.
 * @param options - Clamping options.
 * @returns Screen coordinates and the placement that was actually used.
 */
export function clampPopupPosition(
  anchor: PopupAnchor | AnchorRect,
  popup: PopupDimensions,
  placement: PopupPlacement,
  options: ClampPopupOptions = {},
): ClampPopupResult {
  const anchorRect = getAnchorRect(anchor);
  const offset = options.offset ?? 4;
  const margin = options.margin ?? POPUP_VIEWPORT_MARGIN;
  const viewport = options.viewport ?? getDefaultViewport();
  const effectiveHeight = popup.estimatedHeight ?? popup.minHeight ?? POPUP_MIN_VISIBLE_HEIGHT;
  const clampedPopup: PopupDimensions = { ...popup, estimatedHeight: effectiveHeight };

  const candidates = getCandidatePlacements(placement);

  for (const candidate of candidates) {
    const raw = computePlacement(candidate, anchorRect, clampedPopup, offset);
    const fitsHorizontally = raw.left >= margin && raw.left + popup.width <= viewport.width - margin;
    const fitsVertically = raw.top >= margin && raw.top + effectiveHeight <= viewport.height - margin;

    if (fitsHorizontally && fitsVertically) {
      return { left: raw.left, top: raw.top, placement: candidate };
    }
  }

  // No placement fits cleanly; use the first candidate and adjust.
  const primary = candidates[0] ?? 'bottom-start';
  const raw = computePlacement(primary, anchorRect, clampedPopup, offset);

  // For cursor-based menus, flip horizontally/vertically before clamping so the
  // menu opens away from the viewport edge the user clicked near.
  if (primary === 'cursor') {
    let left = raw.left;
    let top = raw.top;
    if (left + popup.width + margin > viewport.width) {
      left = raw.left - popup.width;
    }
    if (top + effectiveHeight + margin > viewport.height) {
      top = raw.top - effectiveHeight;
    }
    return {
      left: Math.max(margin, Math.min(left, viewport.width - popup.width - margin)),
      top: Math.max(margin, Math.min(top, viewport.height - effectiveHeight - margin)),
      placement: 'cursor',
    };
  }

  const left = Math.max(margin, Math.min(raw.left, viewport.width - popup.width - margin));
  const top = Math.max(margin, Math.min(raw.top, viewport.height - effectiveHeight - margin));

  return { left, top, placement: primary };
}

/**
 * Build a fixed-position style object for a popup that has been clamped to the
 * viewport. The returned object sets a CSS custom property so the popup can use
 * `max-height: calc(100vh - var(--positioned-popup-top) - 16px)` and therefore
 * scroll rather than extend past the bottom of a short screen.
 *
 * @param left - Horizontal screen coordinate in pixels.
 * @param top - Vertical screen coordinate in pixels.
 * @param options - Optional margin and z-index overrides.
 * @returns A style object ready to spread onto a `position: fixed` element.
 */
export function getPopupStyle(
  left: number,
  top: number,
  options: { margin?: number; zIndex?: number } = {},
): React.CSSProperties {
  const margin = options.margin ?? POPUP_VIEWPORT_MARGIN;
  const cssVarKey = POPUP_TOP_CSS_VAR as string;

  return {
    position: 'fixed',
    left,
    top,
    zIndex: options.zIndex ?? 1000,
    [cssVarKey]: `${top}px`,
    maxHeight: `calc(100vh - var(${POPUP_TOP_CSS_VAR}) - ${margin * 2}px)`,
    overflowY: 'auto',
  } as React.CSSProperties;
}

/**
 * Convenience helper that resolves an anchor, clamps the popup, and returns a
 * ready-to-use style object. Useful for callers that just need the final style.
 *
 * @param anchor - Anchor element/ref/point.
 * @param popup - Estimated popup dimensions.
 * @param placement - Desired placement.
 * @param options - Clamping and style options.
 */
export function getPositionedPopupStyle(
  anchor: PopupAnchor | AnchorRect,
  popup: PopupDimensions,
  placement: PopupPlacement,
  options: ClampPopupOptions & { zIndex?: number } = {},
): React.CSSProperties {
  const anchorRect = getAnchorRect(anchor);
  const pos = clampPopupPosition(anchorRect, popup, placement, options);
  return getPopupStyle(pos.left, pos.top, { margin: options.margin, zIndex: options.zIndex });
}

/**
 * Legacy alias for `clampPopupPosition` used by older DataGrid code.
 * Kept for backwards compatibility; new code should use `clampPopupPosition`.
 */
export const clampMenuPosition = clampPopupPosition;
